import { ModalPortal } from './ModalPortal';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Paperclip,
  Calendar,
  X,
  Check,
  AlertCircle,
  Upload,
  Edit,
  PieChart,
  AlertTriangle,
  Clock,
  Eye,
  Layers,
  Truck,
  Camera,
  CheckCircle,
  RotateCcw,
  ExternalLink,
  Calculator,
  FileUp,
} from "lucide-react";
import { mockBackend, Revenue } from "../services/mockBackend";
import { GoogleGenAI, Type } from "@google/genai";
import { extractDataWithAI } from "../services/aiService";
import { AlertModal } from './AlertModal';
import { getLocalDateString } from '../src/lib/utils';

const Revenues: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedMonth, setSelectedMonth] = useState(
    getLocalDateString().slice(0, 7),
  );

  const { startOfMonth, endOfMonth } = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return {
      startOfMonth: `${y}-${String(m).padStart(2, "0")}-01`,
      endOfMonth: `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
    };
  }, [selectedMonth]);

  const curr = new Date();
  const first = curr.getDate() - curr.getDay();
  const startOfWeek = getLocalDateString(new Date(curr.setDate(first)));
  const endOfWeek = getLocalDateString(new Date(curr.setDate(first + 6)));

  const [activeTab, setActiveTab] = useState<"overview" | "entries">(
    "overview",
  );
  const [agendaFilter, setAgendaFilter] = useState<'all' | 'EM_ABERTO' | 'RECEBIDO' | 'VENCIDO'>('all');
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  const [isLoading, setIsLoading] = useState(true);

  const [alertConfig, setAlertConfig] = useState<{isOpen: boolean, message: string, type: 'error'|'success'|'info'}>({isOpen: false, message: '', type: 'error'});
  const showAlert = (message: string, type: 'error'|'success'|'info' = 'error') => setAlertConfig({isOpen: true, message, type});

  const [filters, setFilters] = useState({
    dateStart: startOfMonth,
    dateEnd: endOfMonth,
    status: "all",
    tipo_nota: "all",
    categoria_receita: "all",
    search: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);

  const [currentRevenue, setCurrentRevenue] = useState<Partial<Revenue>>({
    tipo_nota: "NFE",
    status: "EM_ABERTO",
    data_emissao: getLocalDateString(),
    previsao_pagamento: getLocalDateString(),
    created_at: getLocalDateString(),
    valor_bruto: 0,
    valor_desconto: 0,
    impostos: 0,
    valor_liquido: 0,
  });

  const [amountInput, setAmountInput] = useState("0,00");
  const [discountInput, setDiscountInput] = useState("0,00");
  const [taxInput, setTaxInput] = useState("0,00");
  const [isSaving, setIsSaving] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  useEffect(() => {
    if (user) {
      const unsubscribe = mockBackend.subscribeToRevenues(user, (revs) => {
        setRevenues(revs);
        setIsLoading(false);
      });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    if (revenues.length > 0 && location.state) {
      const state = location.state as any;
      if (state.editRevenueId) {
        const rev = revenues.find(r => r.id === state.editRevenueId);
        if (rev) {
          openModal(rev, false);
          // Clear state so it doesn't reopen on refresh
          navigate(location.pathname, { replace: true });
        }
      } else if (state.viewRevenueId) {
        const rev = revenues.find(r => r.id === state.viewRevenueId);
        if (rev) {
          openModal(rev, true);
          // Clear state so it doesn't reopen on refresh
          navigate(location.pathname, { replace: true });
        }
      }
    }
  }, [revenues, location.state, navigate, location.pathname]);

  const canEdit = (revenue: Revenue | Partial<Revenue>) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!revenue.id) return true;
    return revenue.createdBy === user.id;
  };

  const canDelete = () => {
    return user?.role === "admin";
  };

  const filteredRevenues = useMemo(() => {
    let result = [...revenues];
    if (filters.status !== "all")
      result = result.filter((e) => e.status === filters.status);
    if (filters.tipo_nota !== "all")
      result = result.filter((e) => e.tipo_nota === filters.tipo_nota);
    if (filters.categoria_receita !== "all")
      result = result.filter(
        (e) => e.categoria_receita === filters.categoria_receita,
      );

    if (filters.dateStart) {
      result = result.filter((e) => {
        const dateRef =
          e.status === "RECEBIDO" && e.data_recebimento
            ? e.data_recebimento
            : e.previsao_pagamento || e.data_emissao || e.created_at;
        return dateRef >= filters.dateStart;
      });
    }
    if (filters.dateEnd) {
      result = result.filter((e) => {
        const dateRef =
          e.status === "RECEBIDO" && e.data_recebimento
            ? e.data_recebimento
            : e.previsao_pagamento || e.data_emissao || e.created_at;
        return dateRef <= filters.dateEnd;
      });
    }

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (e) =>
          (e.cliente || "").toLowerCase().includes(term) ||
          (e.descricao || "").toLowerCase().includes(term) ||
          String(e.valor_liquido).includes(term) ||
          (e.numero_nota || "").toLowerCase().includes(term) ||
          (e.serie_nota || "").toLowerCase().includes(term) ||
          (e.tipo_nota || "").toLowerCase().includes(term) ||
          (e.categoria_receita || "").toLowerCase().includes(term)
      );
    }

    result.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return result;
  }, [revenues, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const dashboardMetrics = useMemo(() => {
    const currentMonthRevs = revenues.filter((e) => {
      const dateToCheck =
        e.status === "RECEBIDO"
          ? e.data_recebimento || e.previsao_pagamento
          : e.previsao_pagamento || e.data_emissao || e.created_at;
      if (!dateToCheck) return false;
      const dateStr = dateToCheck.split("T")[0];
      return dateStr >= startOfMonth && dateStr <= endOfMonth;
    });

    const overdueRevs = currentMonthRevs.filter(
      (e) =>
        e.status === "VENCIDO" ||
        (e.status === "EM_ABERTO" &&
          e.previsao_pagamento &&
          e.previsao_pagamento < getLocalDateString()),
    );
    const openRevs = currentMonthRevs.filter(
      (e) =>
        e.status === "EM_ABERTO" &&
        (!e.previsao_pagamento ||
          e.previsao_pagamento >= getLocalDateString()),
    );
    const paidRevs = currentMonthRevs.filter((e) => e.status === "RECEBIDO");

    const totalOverdue = overdueRevs.reduce(
      (sum, e) => sum + e.valor_liquido,
      0,
    );
    const totalOpen = openRevs.reduce((sum, e) => sum + e.valor_liquido, 0);
    const totalPaid = paidRevs.reduce((sum, e) => sum + e.valor_liquido, 0);
    const totalEmitted = currentMonthRevs.reduce(
      (sum, e) => sum + e.valor_liquido,
      0,
    );

    const todayStr = getLocalDateString().slice(0, 7);
    let agendaList = [...currentMonthRevs];

    agendaList.sort((a, b) => {
      const dateA =
        a.status === "RECEBIDO" && a.data_recebimento
          ? a.data_recebimento
          : a.previsao_pagamento || a.data_emissao || a.created_at;
      const dateB =
        b.status === "RECEBIDO" && b.data_recebimento
          ? b.data_recebimento
          : b.previsao_pagamento || b.data_emissao || b.created_at;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.localeCompare(dateB);
    });

    const categoryData: Record<string, number> = {};
    currentMonthRevs.forEach((e) => {
      const val = e.valor_liquido;
      const cat = e.categoria_receita || "Geral";
      categoryData[cat] = (categoryData[cat] || 0) + val;
    });
    const maxVal = Math.max(...Object.values(categoryData), 1);

    return {
      totalOverdue,
      totalOpen,
      totalPaid,
      totalEmitted,
      agenda: agendaList,
      categoryData,
      maxVal,
      count: currentMonthRevs.length,
    };
  }, [revenues, startOfMonth, endOfMonth, selectedMonth]);

  const entriesTotalAmount = useMemo(() => {
    if (!Array.isArray(filteredRevenues)) return 0;
    return filteredRevenues.reduce(
      (sum, exp) => sum + (Number(exp.valor_liquido) || 0),
      0,
    );
  }, [filteredRevenues]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [revenueToDelete, setRevenueToDelete] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    if (!canDelete()) return;
    setRevenueToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!revenueToDelete) return;
    try {
      await mockBackend.deleteRevenue(user!, revenueToDelete);
      setRevenues((prev) => prev.filter((e) => e.id !== revenueToDelete));
      setIsDeleteModalOpen(false);
      setRevenueToDelete(null);
    } catch (e: any) {
      showAlert(e.message);
    }
  };

  const handleAmountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(rawValue || "0", 10) / 100;
    setAmountInput(
      numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

    const currentDiscount = currentRevenue.valor_desconto || 0;
    const currentTax = currentRevenue.impostos || 0;
    setCurrentRevenue((prev) => ({
      ...prev,
      valor_bruto: numberValue,
      valor_liquido: numberValue - currentDiscount - currentTax,
    }));
  };

  const handleDiscountInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(rawValue || "0", 10) / 100;
    setDiscountInput(
      numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

    const currentBruto = currentRevenue.valor_bruto || 0;
    const currentTax = currentRevenue.impostos || 0;
    setCurrentRevenue((prev) => ({
      ...prev,
      valor_desconto: numberValue,
      valor_liquido: currentBruto - numberValue - currentTax,
    }));
  };

  const handleTaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(rawValue || "0", 10) / 100;
    setTaxInput(
      numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

    const currentBruto = currentRevenue.valor_bruto || 0;
    const currentDiscount = currentRevenue.valor_desconto || 0;
    setCurrentRevenue((prev) => ({
      ...prev,
      impostos: numberValue,
      valor_liquido: currentBruto - currentDiscount - numberValue,
    }));
  };

  const openModal = (revenue?: Revenue, forceViewMode = false) => {
    setViewMode(forceViewMode);

    if (revenue) {
      setIsEditing(true);
      setCurrentRevenue(JSON.parse(JSON.stringify(revenue)));
      setAmountInput(
        (revenue.valor_bruto || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      setDiscountInput(
        (revenue.valor_desconto || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      setTaxInput(
        (revenue.impostos || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
    } else {
      setIsEditing(false);
      const todayStr = getLocalDateString();
      setCurrentRevenue({
        tipo_nota: "NFE",
        status: "EM_ABERTO",
        categoria_receita: "Serviços",
        data_emissao: todayStr,
        previsao_pagamento: todayStr,
        created_at: todayStr,
        valor_bruto: 0,
        valor_desconto: 0,
        impostos: 0,
        valor_liquido: 0,
      });
      setAmountInput("0,00");
      setDiscountInput("0,00");
      setTaxInput("0,00");
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit(currentRevenue)) return;
    setIsSaving(true);
    try {
      const finalBruto =
        parseFloat(amountInput.replace(/\./g, "").replace(",", ".")) || 0;
      const finalDesconto =
        parseFloat(discountInput.replace(/\./g, "").replace(",", ".")) || 0;
      const finalTax =
        parseFloat(taxInput.replace(/\./g, "").replace(",", ".")) || 0;
      const finalLiquido = finalBruto - finalDesconto - finalTax;

      const revenueToSave = {
        ...currentRevenue,
        valor_bruto: finalBruto,
        valor_desconto: finalDesconto,
        impostos: finalTax,
        valor_liquido: finalLiquido,
      };

      if (revenueToSave.status === "RECEBIDO") {
        if (!revenueToSave.data_recebimento)
          revenueToSave.data_recebimento =
            revenueToSave.previsao_pagamento ||
            revenueToSave.data_emissao ||
            revenueToSave.created_at;
      }

      if (isEditing && revenueToSave.id) {
        await mockBackend.updateRevenue(user!, revenueToSave.id, revenueToSave);
      } else {
        await mockBackend.addRevenue(
          user!,
          revenueToSave as Omit<Revenue, "id" | "created_at" | "updated_at">,
        );
      }
      setIsModalOpen(false);
      setDocumentMetadata(null);
    } catch (e: any) {
      showAlert("Erro ao salvar: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePdfUploadClick = () => {
    pdfInputRef.current?.click();
  };

  const handlePdfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPdf(true);
    try {
      const base64EncodeString = await new Promise<string>(
        (resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(",")[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        },
      );

      const mimeType = file.type === "application/pdf" ? "application/pdf" : "image/jpeg";
      const prompt = "Extraia os dados desta nota fiscal ou comprovante (incluindo comprovantes de PIX). Preencha todos os campos que o sistema precisa para salvar (cliente, descricao, numero_nota, serie_nota, tipo_nota, data_emissao, previsao_pagamento, valor_bruto, valor_desconto, impostos, valor_liquido, forma_recebimento, categoria_receita, centro_receita, observacoes). Retorne um JSON.";
      const schema = {
        type: Type.OBJECT,
        properties: {
          cliente: {
            type: Type.STRING,
            description: "Nome ou Razão Social do cliente/pagador",
          },
          descricao: {
            type: Type.STRING,
            description: "Descrição do serviço ou produto",
          },
          numero_nota: {
            type: Type.STRING,
            description: "Número da nota fiscal",
          },
          serie_nota: {
            type: Type.STRING,
            description: "Série da nota fiscal",
          },
          tipo_nota: {
            type: Type.STRING,
            description: "NFE, NFSE, ou OUTRO",
          },
          data_emissao: {
            type: Type.STRING,
            description: "Data de emissão no formato YYYY-MM-DD",
          },
          previsao_pagamento: {
            type: Type.STRING,
            description: "Previsão de pagamento no formato YYYY-MM-DD",
          },
          valor_bruto: {
            type: Type.NUMBER,
            description: "Valor bruto total da nota",
          },
          valor_desconto: {
            type: Type.NUMBER,
            description: "Valor total de descontos",
          },
          impostos: {
            type: Type.NUMBER,
            description: "Valor total de impostos retidos",
          },
          valor_liquido: {
            type: Type.NUMBER,
            description:
              "Valor líquido a receber (bruto - descontos - impostos)",
          },
        },
      };

      const extractedData = await extractDataWithAI(base64EncodeString, mimeType, prompt, schema);

      const validTiposNota = ["NFE", "NFSE", "OUTRO"];
      const validCategorias = ["Serviços", "Produtos", "Consultoria", "Outros"];

      const revenueData: any = {};
      if (extractedData.valor_bruto !== undefined) revenueData.valor_bruto = extractedData.valor_bruto;
      if (extractedData.data_emissao) revenueData.data_emissao = extractedData.data_emissao;
      if (extractedData.previsao_pagamento) revenueData.previsao_pagamento = extractedData.previsao_pagamento;
      if (extractedData.cliente) revenueData.cliente = extractedData.cliente;
      if (extractedData.numero_nota) revenueData.numero_nota = extractedData.numero_nota;
      if (extractedData.serie_nota) revenueData.serie_nota = extractedData.serie_nota;
      if (extractedData.descricao) revenueData.descricao = extractedData.descricao;
      if (extractedData.valor_desconto !== undefined) revenueData.valor_desconto = extractedData.valor_desconto;
      if (extractedData.impostos !== undefined) revenueData.impostos = extractedData.impostos;
      if (extractedData.valor_liquido !== undefined) revenueData.valor_liquido = extractedData.valor_liquido;
      if (extractedData.forma_recebimento) revenueData.forma_recebimento = extractedData.forma_recebimento;
      if (extractedData.centro_receita) revenueData.centro_receita = extractedData.centro_receita;
      if (extractedData.observacoes) revenueData.observacoes = extractedData.observacoes;

      const extractedTipo = extractedData.tipo_nota || "NFE";
      const extractedCategoria = extractedData.categoria_receita || "Serviços";

      setCurrentRevenue((prev) => ({
        ...prev,
        ...revenueData,
        tipo_nota: validTiposNota.includes(extractedTipo) ? extractedTipo : (prev.tipo_nota || "NFE"),
        categoria_receita: validCategorias.includes(extractedCategoria) ? extractedCategoria : (prev.categoria_receita || "Serviços"),
      }));

      if (revenueData.valor_bruto !== undefined) {
        setAmountInput(
          revenueData.valor_bruto.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        );
      }
      if (revenueData.valor_desconto !== undefined) {
        setDiscountInput(
          revenueData.valor_desconto.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        );
      }
      if (revenueData.impostos !== undefined) {
        setTaxInput(
          revenueData.impostos.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        );
      }

      if (extractedData._metadata) {
        setDocumentMetadata(extractedData._metadata);
      }

      showAlert(
        "Dados extraídos do documento com sucesso via IA. Por favor, revise os campos antes de salvar.",
        "success"
      );

      if (!isModalOpen) {
        setIsModalOpen(true);
        setIsEditing(false);
      }
    } catch (err: any) {
      showAlert("Erro ao ler documento com IA: " + err.message);
    } finally {
      setIsUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handleStatusChange = async (revenue: Revenue, newStatus: string) => {
    if (!canEdit(revenue)) return;
    try {
      const updates: Partial<Revenue> = { status: newStatus as any };
      if (newStatus === "RECEBIDO" && revenue.status !== "RECEBIDO") {
        updates.data_recebimento = getLocalDateString();
      } else if (newStatus !== "RECEBIDO") {
        updates.data_recebimento = undefined;
      }
      await mockBackend.updateRevenue(user!, revenue.id, updates);
    } catch (err: any) {
      showAlert(err.message);
    }
  };

  const getStatusSelect = (revenue: Revenue) => {
    let bgClass = "bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20";
    if (revenue.status === "RECEBIDO") bgClass = "bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/20";
    if (revenue.status === "VENCIDO") bgClass = "bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20";
    if (revenue.status === "CANCELADO") bgClass = "bg-slate-100 dark:bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-200 dark:border-slate-500/20";

    return (
      <select
        value={revenue.status}
        onChange={(e) => handleStatusChange(revenue, e.target.value)}
        disabled={!canEdit(revenue)}
        className={`appearance-none cursor-pointer outline-none inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase border shadow-sm ${bgClass}`}
      >
        <option value="EM_ABERTO">Aberto</option>
        <option value="RECEBIDO">Recebido</option>
        <option value="VENCIDO">Vencido</option>
        <option value="CANCELADO">Cancelado</option>
      </select>
    );
  };

  const formatCurrency = (val: number | undefined | null) => {
    if (typeof val !== "number" || isNaN(val)) return "R$ 0,00";
    return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "-";
    const dateToParse = dateString.includes("T") ? dateString : `${dateString}T12:00:00`;
    return new Date(dateToParse).toLocaleDateString("pt-BR");
  };

  const isViewOnly =
    viewMode || (currentRevenue.id && !canEdit(currentRevenue));

  return (
    <div className="space-y-4">
      <AlertModal 
        isOpen={alertConfig.isOpen} 
        message={alertConfig.message} 
        type={alertConfig.type} 
        onClose={() => setAlertConfig(prev => ({...prev, isOpen: false}))} 
      />
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === "overview" ? "border-brand-600 text-brand-700 dark:text-brand-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-200"}`}
        >
          Visão Geral
        </button>
        <button
          onClick={() => setActiveTab("entries")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === "entries" ? "border-brand-600 text-brand-700 dark:text-brand-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-200"}`}
        >
          Lançamentos
        </button>
      </div>

      {activeTab === "overview" && (
        <div className="animate-in fade-in duration-300 space-y-6">
          <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-100 dark:bg-brand-900/50 text-brand-700 dark:text-brand-400 rounded-lg">
                <Calendar size={20} />
              </div>
              <div>
                <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm">
                  Período de Análise (Receitas)
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Selecione o mês para visualizar os indicadores.
                </p>
              </div>
            </div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-sm font-bold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-950 focus:outline-none focus:border-brand-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-purple-50 dark:bg-purple-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total Emitido
              </h3>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 z-10">
                {formatCurrency(dashboardMetrics.totalEmitted)}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-green-50 dark:bg-green-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total Recebido
              </h3>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400 z-10">
                {formatCurrency(dashboardMetrics.totalPaid)}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50 dark:bg-blue-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total em Aberto
              </h3>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 z-10">
                {formatCurrency(dashboardMetrics.totalOpen)}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-red-50 dark:bg-red-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total Vencido
              </h3>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400 z-10">
                {formatCurrency(dashboardMetrics.totalOverdue)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col h-[500px] lg:h-[calc(100vh-380px)] min-h-[400px]">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <PieChart
                  size={18}
                  className="text-brand-600 dark:text-brand-400"
                />
                <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">
                  Receitas por Categoria (Mês)
                </h3>
              </div>
              <div className="space-y-4 flex-1 overflow-y-auto pr-2">
                {Object.entries(dashboardMetrics.categoryData).map(
                  ([cat, val]) => (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {cat}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400">
                          {formatCurrency(val as number)}
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden relative">
                        <div
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-emerald-600"
                          style={{
                            width: `${((val as number) / dashboardMetrics.maxVal) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ),
                )}
                {Object.keys(dashboardMetrics.categoryData).length === 0 && (
                  <div className="text-center text-slate-400 dark:text-slate-500 py-8 text-xs">
                    Sem dados para este mês.
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col h-[500px] lg:h-[calc(100vh-380px)] min-h-[400px]">
              <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <div className="flex items-center gap-2">
                  <Clock
                    size={18}
                    className="text-green-600 dark:text-green-400"
                  />
                  <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">
                    Agenda de Recebimentos (Mês)
                  </h3>
                </div>
                <select 
                  value={agendaFilter} 
                  onChange={(e) => setAgendaFilter(e.target.value as any)}
                  className="text-xs border border-slate-200 dark:border-slate-700 rounded px-2 py-1 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                >
                  <option value="all">Todos</option>
                  <option value="EM_ABERTO">Aberto</option>
                  <option value="RECEBIDO">Recebidos</option>
                  <option value="VENCIDO">Vencidos</option>
                </select>
              </div>
              <div className="space-y-2 flex-1 overflow-y-auto pr-2">
                {dashboardMetrics.agenda.filter(e => agendaFilter === 'all' || e.status === agendaFilter).length === 0 ? (
                  <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                    <Check size={24} className="mx-auto mb-2 opacity-50" />
                    <p className="text-xs">Nenhum lançamento para exibir.</p>
                  </div>
                ) : (
                  dashboardMetrics.agenda.filter(e => agendaFilter === 'all' || e.status === agendaFilter).map((e) => {
                    let bgClass =
                      "bg-blue-50 dark:bg-blue-500/10 border-blue-500";
                    let textClass = "text-blue-900 dark:text-blue-400";
                    let icon = <Clock size={10} />;
                    let label = "Aberto";
                    let dateShow = e.previsao_pagamento
                      ? formatDate(e.previsao_pagamento)
                      : "N/A";

                    if (e.status === "RECEBIDO") {
                      bgClass =
                        "bg-green-50 dark:bg-green-500/10 border-green-500";
                      textClass = "text-green-900 dark:text-green-400";
                      icon = <CheckCircle size={10} />;
                      label = "Recebido";
                      if (e.data_recebimento)
                        dateShow = formatDate(e.data_recebimento);
                    } else if (e.status === "VENCIDO") {
                      bgClass = "bg-red-50 dark:bg-red-500/10 border-red-500";
                      textClass = "text-red-900 dark:text-red-400";
                      icon = <AlertTriangle size={10} />;
                      label = "Vencido";
                    }

                    return (
                      <div
                        key={e.id}
                        className={`flex justify-between items-center p-3 border-l-2 rounded text-sm \${bgClass}`}
                      >
                        <div className="cursor-pointer hover:opacity-80" onClick={() => openModal(e, true)}>
                          <span className={`block font-semibold \${textClass}`}>
                            {e.cliente} - {e.descricao}
                          </span>
                          <span
                            className={`text-[10px] font-bold uppercase flex items-center gap-1 \${textClass} opacity-80`}
                          >
                            {icon} {label}: {dateShow}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className={`font-bold text-sm \${textClass}`}>
                            {formatCurrency(e.valor_liquido)}
                          </div>
                          <div onClick={ev => ev.stopPropagation()}>
                            {getStatusSelect(e)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "entries" && (
        <div className="animate-in fade-in duration-300 space-y-4 pb-12">
          <div className="flex flex-col md:flex-row justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-2.5 text-slate-400 dark:text-slate-500"
                size={16}
              />
              <input
                type="text"
                placeholder="Buscar por cliente, descrição, nota..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 rounded text-sm focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              <input
                type="file"
                ref={pdfInputRef}
                onChange={handlePdfChange}
                accept="application/pdf,image/*"
                className="hidden"
              />
              <button
                onClick={handlePdfUploadClick}
                disabled={isUploadingPdf}
                className="bg-blue-600 text-white px-4 py-2 rounded font-bold uppercase text-xs flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
              >
                <FileUp size={16} />{" "}
                {isUploadingPdf ? "Lendo..." : "Preencher via IA"}
              </button>
              <button
                onClick={() => openModal()}
                className="bg-green-600 text-white px-4 py-2 rounded font-bold uppercase text-xs flex items-center gap-2 hover:bg-green-700 transition-colors shadow-sm"
              >
                <Plus size={16} /> Novo
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            >
              <option value="all">Status: Todos</option>
              <option value="EM_ABERTO">Aberto</option>
              <option value="RECEBIDO">Recebido</option>
              <option value="VENCIDO">Vencido</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
            <select
              value={filters.tipo_nota}
              onChange={(e) =>
                setFilters({ ...filters, tipo_nota: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            >
              <option value="all">Tipo: Todos</option>
              <option value="NFE">NFE</option>
              <option value="NFSE">NFSE</option>
              <option value="OUTRO">OUTRO</option>
            </select>
            <input
              type="date"
              value={filters.dateStart}
              onChange={(e) =>
                setFilters({ ...filters, dateStart: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            />
            <input
              type="date"
              value={filters.dateEnd}
              onChange={(e) =>
                setFilters({ ...filters, dateEnd: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            />
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Emissão</th>
                  <th className="px-4 py-3 font-semibold">Vencimento</th>
                  <th className="px-4 py-3 font-semibold">
                    Cliente / Descrição
                  </th>
                  <th className="px-4 py-3 font-semibold">Nota</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Valor Líquido
                  </th>
                  <th className="px-4 py-3 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-sm text-slate-700 dark:text-slate-300">
                {filteredRevenues.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((rev) => (
                  <tr
                    key={rev.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-2">
                      {getStatusSelect(rev)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {rev.data_emissao
                        ? formatDate(rev.data_emissao)
                        : "-"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {rev.previsao_pagamento
                        ? formatDate(rev.previsao_pagamento)
                        : "-"}
                    </td>
                    <td
                      className="px-4 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      onClick={() => openModal(rev, true)}
                      title="Clique para visualizar detalhes"
                    >
                      <div className="font-semibold text-slate-800 dark:text-slate-200">
                        {rev.cliente}
                      </div>
                      <div className="text-xs text-slate-400 dark:text-slate-500">
                        {rev.descricao}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-500/20 font-medium">
                        {rev.numero_nota || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="font-mono font-medium text-slate-800 dark:text-slate-200">
                        {formatCurrency(rev.valor_liquido)}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openModal(rev, false)}
                          className="p-1.5 text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 rounded"
                          title={canEdit(rev) ? "Editar" : "Ver"}
                        >
                          {canEdit(rev) ? (
                            <Edit size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                        {canDelete() && (
                          <button
                            onClick={() => handleDeleteClick(rev.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredRevenues.length > itemsPerPage && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-slate-300 dark:border-slate-700 disabled:opacity-50 text-sm"
                >
                  Anterior
                </button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Página {currentPage} de {Math.ceil(filteredRevenues.length / itemsPerPage)}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredRevenues.length / itemsPerPage), p + 1))}
                  disabled={currentPage === Math.ceil(filteredRevenues.length / itemsPerPage)}
                  className="px-3 py-1 rounded border border-slate-300 dark:border-slate-700 disabled:opacity-50 text-sm"
                >
                  Próxima
                </button>
              </div>
            )}

            <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 px-4 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Calculator
                  size={18}
                  className="text-brand-600 dark:text-brand-400"
                />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Total Filtrado
                </span>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-6 py-2 rounded-lg shadow-inner">
                <span className="text-xl font-bold text-brand-700 dark:text-brand-400 font-mono">
                  {formatCurrency(entriesTotalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-red-600 text-white px-5 py-3 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                <Trash2 size={18} /> Confirmar Exclusão
              </h3>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="hover:text-red-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Confirmar exclusão definitiva?</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Esta ação não poderá ser desfeita. O lançamento será removido permanentemente.</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm text-white bg-red-600 hover:bg-red-700 font-bold uppercase rounded shadow-sm"
                >
                  Sim, Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 z-[9999] flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-5xl rounded-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-[#4c1d95] via-[#5b21b6] to-[#3b0764] text-white px-6 py-3.5 flex justify-between items-center shrink-0 shadow-md">
              <h3 className="font-bold text-base flex items-center gap-2 tracking-wide">
                {isEditing ? (
                  isViewOnly ? (
                    <Eye size={18} />
                  ) : (
                    <Edit size={18} />
                  )
                ) : (
                  <Plus size={18} />
                )}
                {isEditing
                  ? isViewOnly
                    ? "Visualizar Receita"
                    : "Editar Receita"
                  : "Nova Receita"}
              </h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setDocumentMetadata(null);
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              {documentMetadata && (
                <div className={`p-4 rounded-xl border ${documentMetadata.precisa_revisao ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-200' : 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800/50 dark:text-green-200'}`}>
                  <div className="flex items-center gap-2 font-semibold mb-2">
                    {documentMetadata.precisa_revisao ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                    <span>
                      {documentMetadata.precisa_revisao ? 'Revisão Necessária' : 'Extração Confiável'}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><strong>Tipo de Documento:</strong> {documentMetadata.tipo_documento}</p>
                    <p><strong>Confiança Média:</strong> {(documentMetadata.score_confianca * 100).toFixed(0)}%</p>
                    {documentMetadata.erros_validacao?.length > 0 && (
                      <div className="mt-2 text-red-600 dark:text-red-400">
                        <strong>Avisos de Validação:</strong>
                        <ul className="list-disc pl-5 mt-1">
                          {documentMetadata.erros_validacao.map((erro: string, idx: number) => (
                            <li key={idx}>{erro}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Coluna da Esquerda: Cliente e Documento (7 colunas) */}
                <div className="lg:col-span-7 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Dados do Cliente e Documento
                  </h4>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Cliente
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg focus:border-[#7c3aed] focus:outline-none disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                      placeholder="Nome do Cliente"
                      value={currentRevenue.cliente || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          cliente: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Descrição
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] focus:outline-none"
                      placeholder="Descrição do serviço/produto"
                      value={currentRevenue.descricao || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          descricao: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Tipo de Nota
                      </label>
                      <select
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                        value={currentRevenue.tipo_nota}
                        onChange={(e) =>
                          setCurrentRevenue({
                            ...currentRevenue,
                            tipo_nota: e.target.value,
                          })
                        }
                      >
                        <option value="NFE">NFE</option>
                        <option value="NFSE">NFSE</option>
                        <option value="OUTRO">OUTRO</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Status
                      </label>
                      <select
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                        value={currentRevenue.status}
                        onChange={(e) =>
                          setCurrentRevenue({
                            ...currentRevenue,
                            status: e.target.value as any,
                          })
                        }
                      >
                        <option value="EM_ABERTO">Em Aberto</option>
                        <option value="RECEBIDO">Recebido</option>
                        <option value="VENCIDO">Vencido</option>
                        <option value="CANCELADO">Cancelado</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Número da Nota
                      </label>
                      <input
                        type="text"
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] focus:outline-none"
                        value={currentRevenue.numero_nota || ""}
                        onChange={(e) =>
                          setCurrentRevenue({
                            ...currentRevenue,
                            numero_nota: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Série da Nota
                      </label>
                      <input
                        type="text"
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] focus:outline-none"
                        value={currentRevenue.serie_nota || ""}
                        onChange={(e) =>
                          setCurrentRevenue({
                            ...currentRevenue,
                            serie_nota: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Coluna da Direita: Valores e Prazos (5 colunas) */}
                <div className="lg:col-span-5 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Valores e Prazos
                  </h4>

                  <div className="bg-purple-50/50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/40 rounded-xl p-4 space-y-3.5">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                        Valor Bruto (R$)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        disabled={isViewOnly}
                        className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none"
                        value={amountInput}
                        onChange={handleAmountInputChange}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                          Desconto (R$)
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          disabled={isViewOnly}
                          className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg font-bold text-slate-700 dark:text-slate-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-[#7c3aed] outline-none text-sm"
                          value={discountInput}
                          onChange={handleDiscountInputChange}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                          Impostos (R$)
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          disabled={isViewOnly}
                          className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg font-bold text-slate-700 dark:text-slate-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-[#7c3aed] outline-none text-sm"
                          value={taxInput}
                          onChange={handleTaxInputChange}
                        />
                      </div>
                    </div>

                    <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-purple-100 dark:border-purple-900/40">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-0.5">
                        Valor Líquido (R$)
                      </label>
                      <div className="font-mono text-xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(currentRevenue.valor_liquido)}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                          Data de Emissão
                        </label>
                        <input
                          type="date"
                          disabled={isViewOnly}
                          className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs"
                          value={currentRevenue.data_emissao || ""}
                          onChange={(e) =>
                            setCurrentRevenue({
                              ...currentRevenue,
                              data_emissao: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                          Previsão Pagto
                        </label>
                        <input
                          type="date"
                          disabled={isViewOnly}
                          className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs"
                          value={currentRevenue.previsao_pagamento || ""}
                          onChange={(e) =>
                            setCurrentRevenue({
                              ...currentRevenue,
                              previsao_pagamento: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/70 border-t border-slate-200 dark:border-slate-700/80 px-6 py-3.5 flex justify-end gap-3 shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setDocumentMetadata(null);
                }}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 rounded-lg transition-colors font-medium"
              >
                {isViewOnly ? "Fechar" : "Cancelar"}
              </button>
              {!isViewOnly && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-6 py-2 text-sm text-white bg-green-600 hover:bg-green-700 font-bold uppercase rounded-lg shadow-md flex items-center gap-2 disabled:opacity-50 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isSaving ? "Salvando..." : "Salvar"}
                </button>
              )}
            </div>
          </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default Revenues;
